import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as I from "../../Config/interface.config";
import APIRequest from "../../API";
import * as APIPATH from "../../API/path";
import { messageScrollHandler } from "../../Utils";

type ChannelMessages = {
  messages: I.Message[];
  loading: boolean;
  error: string | null;
  page: number;
  hasMore: boolean;
};

type MessageState = {
  subscribers: I.Subscriber[];
  channels: I.Channel[];
  loading: boolean;
  activeChat: string;
  messageLoading: boolean;
  paymentModal: boolean;
  channelsLoading: boolean;
  channelsError: string | null;
  messagesByChannel: Record<string, ChannelMessages>;
};

const updateChannelLatestMessage = (channels: I.Channel[], message: I.Message) => {
  const channelSlug = message?.channelSlug;
  if (!channelSlug) return;

  const channel = channels.find((item) => item.channelSlug === channelSlug);
  if (!channel) return;

  const existingMessages = channel.messages || [];
  const messageId = message._id;
  const withoutDuplicate = messageId
    ? existingMessages.filter((item) => item._id !== messageId)
    : existingMessages;

  channel.messages = [message, ...withoutDuplicate];
  channel.lastMessageOn = message.createdAt || channel.lastMessageOn;
  channel.updatedAt = message.createdAt || channel.updatedAt;
};


const hasSameMessage = (messages: I.Message[], message: I.Message) => {
  if (message._id) {
    return messages.some((item) => item._id === message._id);
  }

  return messages.some(
    (item) =>
      item.text === message.text &&
      item.channelSlug === message.channelSlug &&
      item.createdAt === message.createdAt
  );
};

const ensureChannelMessages = (state: MessageState, channelSlug: string) => {
  if (!(channelSlug in state.messagesByChannel)) {
    state.messagesByChannel[channelSlug] = {
      messages: [],
      loading: false,
      error: null,
      page: 1,
      hasMore: true,
    };
  }

  return state.messagesByChannel[channelSlug];
};

const appendMessageToChannel = (state: MessageState, message: I.Message) => {
  const channelSlug = message.channelSlug;
  if (!channelSlug) return false;

  const channelMessages = ensureChannelMessages(state, channelSlug);
  if (hasSameMessage(channelMessages.messages, message)) {
    return false;
  }

  channelMessages.messages.push(message);
  return true;
};

const initialState: MessageState = {
  subscribers: [],
  channels: [],
  loading: false,
  activeChat: "",
  messageLoading: false,
  paymentModal: false,
  channelsLoading: false,
  channelsError: null,
  messagesByChannel: {},
};

export const fetchChannels = createAsyncThunk<I.Channel[]>(
  "messages/fetch-channels",
  async (_, { rejectWithValue }) => {
    try {
      const res = await APIRequest(APIPATH.getUserChannelsWithMessages);
      return res.data.data as I.Channel[];
    } catch (e: any) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  }
);

export const fetchChannelMessages = createAsyncThunk(
  "messages/fetch-channel-messages",
  async (
    { channelSlug, page = 1, limit = 20 }: { channelSlug: string, page?: number, limit?: number },
    { rejectWithValue }
  ) => {
    try {
      const path = { ...APIPATH.getChannelUnreadMessages };
      path.url = path.url.replace("{{channelSlug}}", channelSlug);
      const res =  await APIRequest(path, null, { page, limit });
      return {
        channelSlug,
        messages: res.data.data as I.Message[],
        page,
        hasMore: res.data.meta.hasMore,
        totalCount: res.data.meta.totalCount,
        totalPages: res.data.meta.totalPages,
      };
    } catch (e: any) {
      return rejectWithValue({
        channelSlug,
        error: e?.response?.data || e.message,
      });
    }
  }
);

export const sendMessage = createAsyncThunk(
  "messages/send-message",
  async (newmessage: I.Message, { rejectWithValue }) => {
    try {
      const { data } = await APIRequest(APIPATH.newMessage, newmessage);
      return data as I.Message;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const markMessagesAsRead = createAsyncThunk<any, { channelSlug: string }>(
  "messages/mark-messages-as-read",
  async (payload, { rejectWithValue }) => {
    try {
      const path = { ...APIPATH.markMessagesAsRead };
      const channelslug = payload.channelSlug;
      path.url = path.url.replace("{{channelSlug}}", channelslug);
      const { data } = await APIRequest(path);
      return { channelslug, data };
    } catch (error: any) {
      return rejectWithValue({ reason: error.message ?? error.response?.data });
    }
  }
);

export const createMessageChannel = createAsyncThunk<
  any,
  I.CreaeteMessageChannel
>(
  "messages/create-message-channel",
  async (bodyParams, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await APIRequest(APIPATH.newMessageChannel, bodyParams);
      return data;
    } catch (error: any) {
      return rejectWithValue({ reason: error.message ?? error.response.data });
    }
  }
);

const messageSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    selectChannel: (state, { payload }: PayloadAction<{ channelSlug: string }>) => {
      state.activeChat = payload.channelSlug;
    },
    appendNewMessage: (state, { payload }: PayloadAction<{ newmessage: I.Message }>) => {
      const newMsg = payload.newmessage;
      appendMessageToChannel(state, newMsg);
      updateChannelLatestMessage(state.channels, newMsg);
      messageScrollHandler();
    },
    togglePaymentModal: (state) => {
      state.paymentModal = !state.paymentModal;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChannels.pending, (state) => {
        state.channelsLoading = true;
        state.channelsError = null;
      })
      .addCase(fetchChannels.fulfilled, (state, { payload }) => {
        state.channelsLoading = false;
        state.channels = payload;
      })
      .addCase(fetchChannels.rejected, (state, action) => {
        state.channelsLoading = false;
        state.channelsError = action.payload as string;
      });

    builder
      .addCase(fetchChannelMessages.pending, (state, { meta }) => {
        const channelSlug = meta.arg.channelSlug;
        if (!state.messagesByChannel[channelSlug]) {
          state.messagesByChannel[channelSlug] = {
            messages: [],
            loading: true,
            error: null,
            page: 1,
            hasMore: true,
          };
        } else {
          state.messagesByChannel[channelSlug].loading = true;
          state.messagesByChannel[channelSlug].error = null;
        }
      })
      .addCase(fetchChannelMessages.fulfilled, (state, { payload }) => {
        const { channelSlug, messages, page, hasMore } = payload;
        if (page === 1) {
          state.messagesByChannel[channelSlug] = {
            messages,
            loading: false,
            error: null,
            page,
            hasMore,
          };
        } else {
          state.messagesByChannel[channelSlug].messages = [
            ...messages,
            ...state.messagesByChannel[channelSlug].messages,
          ];
          state.messagesByChannel[channelSlug].loading = false;
          state.messagesByChannel[channelSlug].error = null;
          state.messagesByChannel[channelSlug].page = page;
          state.messagesByChannel[channelSlug].hasMore = hasMore;
        }
      })
      .addCase(fetchChannelMessages.rejected, (state, { payload, meta }) => {
        const channelSlug = meta.arg.channelSlug;
        if (!state.messagesByChannel[channelSlug]) {
          state.messagesByChannel[channelSlug] = {
            messages: [],
            loading: false,
            error: null,
            page: 1,
            hasMore: true,
          };
        }
        state.messagesByChannel[channelSlug].loading = false;
        state.messagesByChannel[channelSlug].error =
          (payload as any)?.error || "Failed to load messages";
      });

    builder
      .addCase(sendMessage.pending, (state) => {
        state.messageLoading = true;
      })
      .addCase(sendMessage.fulfilled, (state, { payload }) => {
        state.messageLoading = false;
        const sentMessage = payload as I.Message;
        appendMessageToChannel(state, sentMessage);
        updateChannelLatestMessage(state.channels, sentMessage);
      })
      .addCase(sendMessage.rejected, (state) => {
        state.messageLoading = false;
      });

    builder
      .addCase(markMessagesAsRead.pending, (state) => {
        state.messageLoading = true;
      })
      .addCase(markMessagesAsRead.fulfilled, (state) => {
        state.messageLoading = false;
      })
      .addCase(markMessagesAsRead.rejected, (state) => {
        state.messageLoading = false;
      });

    builder
      .addCase(createMessageChannel.pending, (state) => {
        state.messageLoading = true;
      })
      .addCase(createMessageChannel.fulfilled, (state) => {
        state.messageLoading = false;
      })
      .addCase(createMessageChannel.rejected, (state) => {
        state.messageLoading = false;
      });
  },
});

export const {
  selectChannel,
  appendNewMessage,
  togglePaymentModal,
} = messageSlice.actions;

export default messageSlice.reducer;
